<template>
  <div class="crmeb-menu">
    <!-- 一级菜单（窄列图标） -->
    <div class="menu-one-wrap">
      <div class="menu-logo">
        <router-link to="/">外贸</router-link>
      </div>
      <div class="menu-one">
        <div
          v-for="item in menus"
          :key="item.key"
          class="menu-one-item"
          :class="{ active: item.key === activeOneKey }"
          @click="onSelectOne(item)"
        >
          <component :is="iconMap[item.icon]" class="menu-one-icon" />
          <span class="menu-one-title">{{ item.title }}</span>
        </div>
      </div>
    </div>

    <!-- 二级菜单（宽列） -->
    <div class="menu-two" :class="{ collapsed: twoCollapsed }">
      <div class="menu-two-inner" v-show="!twoCollapsed">
        <div class="menu-two-head">
          <i class="menu-two-head-bar"></i>
          <span>{{ activeOne?.title || '菜单' }}</span>
        </div>
        <div class="menu-two-list">
          <template v-if="activeOne?.path && !activeOne?.children?.length">
            <div
              class="menu-two-item"
              :class="{ active: route.path === activeOne.path }"
              @click="goTo(activeOne.path)"
            >
              <span class="menu-two-dot"></span>
              <span class="menu-two-text">{{ activeOne.title }}</span>
            </div>
          </template>
          <template v-else>
            <template v-for="child in activeOne?.children || []" :key="child.key">
              <template v-if="child.children?.length">
                <div class="menu-two-group">{{ child.title }}</div>
                <div
                  v-for="leaf in child.children"
                  :key="leaf.key"
                  class="menu-two-item menu-two-item-nested"
                  :class="{ active: route.path === leaf.path }"
                  @click="goTo(leaf.path)"
                >
                  <span class="menu-two-dot"></span>
                  <span class="menu-two-text">{{ leaf.title }}</span>
                </div>
              </template>
              <div
                v-else
                class="menu-two-item"
                :class="{ active: route.path === child.path }"
                @click="goTo(child.path)"
              >
                <span class="menu-two-dot"></span>
                <span class="menu-two-text">{{ child.title }}</span>
              </div>
            </template>
          </template>
        </div>
      </div>
      <div
        class="menu-two-toggle"
        @click="twoCollapsed = !twoCollapsed"
        :title="twoCollapsed ? '展开菜单' : '收起菜单'"
      >
        <MenuFoldOutlined v-if="!twoCollapsed" />
        <MenuUnfoldOutlined v-else />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  DesktopOutlined,
  CloudServerOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  PictureOutlined,
  GlobalOutlined,
  TagsOutlined,
  FolderOpenOutlined,
  MenuOutlined,
  TeamOutlined,
  SettingOutlined,
  RobotOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons-vue'
import { sideMenus, findMenuByPath } from '@/config/menu'

const iconMap = {
  DesktopOutlined,
  CloudServerOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  PictureOutlined,
  GlobalOutlined,
  TagsOutlined,
  FolderOpenOutlined,
  MenuOutlined,
  TeamOutlined,
  SettingOutlined,
  RobotOutlined,
}

const menus = sideMenus
const route = useRoute()
const router = useRouter()
const twoCollapsed = ref(false)
const activeOneKey = ref(sideMenus[0].key)

const activeOne = computed(() => menus.find((m) => m.key === activeOneKey.value) || menus[0])

function syncFromRoute(path) {
  const { parent } = findMenuByPath(path)
  if (parent) activeOneKey.value = parent.key
}

function getFirstLeafPath(item) {
  if (!item) return null
  if (item.path && !item.children?.length) return item.path
  for (const child of item.children || []) {
    const path = getFirstLeafPath(child)
    if (path) return path
  }
  return null
}

function pathInChildren(children, path) {
  for (const child of children || []) {
    if (child.path === path) return true
    if (pathInChildren(child.children, path)) return true
  }
  return false
}

function onSelectOne(item) {
  activeOneKey.value = item.key
  if (item.path && !item.children?.length) {
    goTo(item.path)
    return
  }
  const stillInGroup = pathInChildren(item.children, route.path)
  if (!stillInGroup && item.children?.length) {
    goTo(getFirstLeafPath(item))
  }
}

function goTo(path) {
  if (!path) return
  // 允许「新建对话」在同路径下再次进入时强制刷新会话
  if (path === route.path && path === '/aiAgent/new') {
    router.replace({ path, query: { t: String(Date.now()) } })
    return
  }
  if (path === route.path) return
  router.push(path)
}

watch(
  () => route.path,
  (path) => syncFromRoute(path),
  { immediate: true }
)
</script>

<style scoped>
.crmeb-menu {
  display: flex;
  height: 100%;
  background: #fff;
}

.menu-one-wrap {
  width: 80px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #f0f0f0;
}
.menu-logo {
  height: 64px;
  line-height: 64px;
  text-align: center;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}
.menu-logo a {
  color: #1677ff;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1px;
}
.menu-one {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 0 16px;
}
.menu-one::-webkit-scrollbar {
  width: 0;
}

.menu-one-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 4px 8px;
  padding: 10px 4px;
  border-radius: 6px;
  color: rgba(0, 0, 0, 0.65);
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}
.menu-one-item:hover {
  color: #1677ff;
  background: #e6f4ff;
}
.menu-one-item.active {
  color: #1677ff;
  background: #e6f4ff;
}
.menu-one-icon {
  font-size: 18px;
}
.menu-one-title {
  font-size: 12px;
  line-height: 1.2;
  text-align: center;
  word-break: keep-all;
}

.menu-two {
  position: relative;
  width: 180px;
  flex-shrink: 0;
  background: #ffffff;
  border-right: 1px solid #eef2f7;
  transition: width 0.2s ease;
  overflow: visible;
}
.menu-two.collapsed {
  width: 0;
  border-right: none;
}
.menu-two-inner {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 180px;
}
.menu-two-head {
  height: 64px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
  background: #fff;
  border-bottom: 1px solid #eef2f7;
  flex-shrink: 0;
}
.menu-two-head-bar {
  width: 3px;
  height: 14px;
  border-radius: 2px;
  background: linear-gradient(180deg, #1677ff 0%, #69b1ff 100%);
  flex-shrink: 0;
}
.menu-two-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 10px;
}
.menu-two-list::-webkit-scrollbar {
  width: 4px;
}
.menu-two-list::-webkit-scrollbar-thumb {
  background: #d6e4ff;
  border-radius: 4px;
}
.menu-two-group {
  margin: 8px 8px 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.2;
}
.menu-two-group:first-child {
  margin-top: 0;
}
.menu-two-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  color: rgba(0, 0, 0, 0.65);
  cursor: pointer;
  font-size: 13px;
  line-height: 1.3;
  transition: all 0.18s ease;
  user-select: none;
}
.menu-two-item-nested {
  padding-left: 20px;
}
.menu-two-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c9cdd4;
  flex-shrink: 0;
  transition: all 0.18s ease;
}
.menu-two-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.menu-two-item:hover {
  color: #1677ff;
  background: #fff;
  box-shadow: 0 2px 8px rgba(22, 119, 255, 0.06);
}
.menu-two-item:hover .menu-two-dot {
  background: #69b1ff;
  transform: scale(1.15);
}
.menu-two-item.active {
  color: #1677ff;
  background: #e6f4ff;
  font-weight: 500;
  box-shadow: none;
}
.menu-two-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: #1677ff;
}
.menu-two-item.active .menu-two-dot {
  background: #1677ff;
  box-shadow: 0 0 0 3px rgba(22, 119, 255, 0.15);
}
.menu-two-toggle {
  position: absolute;
  right: -12px;
  top: 50%;
  z-index: 5;
  width: 24px;
  height: 36px;
  margin-top: -18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 0 12px 12px 0;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.04);
  transition: all 0.15s ease;
}
.menu-two.collapsed .menu-two-toggle {
  right: -24px;
}
.menu-two-toggle:hover {
  color: #1677ff;
  background: #e6f4ff;
  border-color: #91caff;
}
</style>
